// caiet UC92 — Verificare stocuri. One medicine (UC92-02) at every IMSP
// Registrar (UC92-01), optionally narrowed to one of them (UC92-03), with the
// site, the last report, the lot, its expiry date and the available quantity
// (UC92-04) and no expired lot in the answer (UC92-05).
//
// Arbitration D2 (wireframes/00-decisions.md, Addendum 2026-08-28): the
// operational reason is mandatory for IMSP staff reading another institution's
// stock, and the System Administrator is exempt. The query is journaled either way.
//
// ACCS-13 as modified 2026-10-12, CF-33: the medicine and the Registrar are in
// the address, the reason never is, and it is asked at every load of the page
// and again whenever the Registrar changes (docs/shape-ops-stock-check-criteria.md).
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import { MudButton, MudInfoBox, MudModal, MudTable, MudTextarea } from '@egov-moldova/mud-react';
import { formatMedicineLabel } from '@simsm/shared-types';
import {
  EmptyRegisterMessage,
  EmptyState,
  formatDate,
  formatExpiry,
  formatNumber,
  onModalDismiss,
} from '@simsm/ui-shared';
import { apiErrorKey, isNormalizedApiError } from '../../app/api-client';
import { useSession } from '../../hooks/use-session';
import { useActiveRegistrar } from '../../hooks/use-active-registrar';
import { MedicineCombobox } from '../../features/reporting/MedicineCombobox';
import { searchMedicines, type MedicineHit } from '../../features/reporting/medicine-api';
import { ImspRegistrarPicker } from '../../features/cross-imsp/ImspRegistrarPicker';
import {
  findImspRegistrar,
  queryCrossImspAvailability,
  type AvailabilityRow,
  type ImspRegistrar,
} from '../../features/cross-imsp/api';
// The look of the shared DialogCombobox the Registrar picker is built on.
import '../operator/reporting/stock-movement-dialog.css';

const REASON_MIN_LENGTH = 10;
const MEDICINE_PARAM = 'medicineCode';
const REGISTRAR_PARAM = 'registrarIdno';

interface Answer {
  medicineCode: string;
  registrarIdno: string;
  rows: AvailabilityRow[];
}

// UC92-04 "data expirării", spelled as it was given: a full date in full even on
// a month's last day, a YYYY-MM lot as its month (docs/shape-ops-stock-check.md D2).
function expiryAsGiven(row: AvailabilityRow): string {
  return formatExpiry(row.lotExpiry, row.lotExpiryPrecision);
}

export function CrossIMSPStockPage(): JSX.Element {
  const { t } = useTranslate();
  const session = useSession();
  const isSystemAdmin = session.data?.activeRole === 'SYSTEM_ADMIN';
  const activeRegistrar = useActiveRegistrar().idno;

  // The criteria are the address's (D4): a bookmark or a reload restores them.
  const [params, setParams] = useSearchParams();
  const medicineCode = params.get(MEDICINE_PARAM)?.trim() ?? '';
  const registrarIdno = params.get(REGISTRAR_PARAM)?.trim() ?? '';
  // What was picked in this visit, so a pick shows its label without a lookup.
  const [pickedMedicine, setPickedMedicine] = useState<MedicineHit | undefined>(undefined);
  const [pickedRegistrar, setPickedRegistrar] = useState<ImspRegistrar | null>(null);
  // CF-52: the address named a Registrar this role may not query, and it was dropped.
  const [registrarDropped, setRegistrarDropped] = useState(false);

  const [reason, setReason] = useState('');
  const [reasonDraft, setReasonDraft] = useState('');
  const [reasonModalOpen, setReasonModalOpen] = useState(false);
  const [reasonTouched, setReasonTouched] = useState(false);
  // The answer keeps the criteria it was given for: one that comes back after
  // they changed is not shown under them. Its code lets a row reported under
  // another code of the same unique medicine say so (docs/shape-ops-stock-check.md D3).
  const [answer, setAnswer] = useState<Answer | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);
  // Article IV — every refusal other than NOT_IMSP_REGISTRAR used to be
  // swallowed: `rows` stayed null, so the screen fell back to the neutral
  // "choose a medicine" prompt and a query that had failed looked exactly like
  // one that was never run.
  const [queryErrorKey, setQueryErrorKey] = useState<string | null>(null);

  const reasonTooShort = reasonDraft.trim().length < REASON_MIN_LENGTH;

  // D6: the reason belongs to the Registrars it was given for. A change of the
  // target or of the active Registrar drops it and the answer, so the next query
  // asks again; a new active Registrar also revalidates the target (CF-52).
  const [seenActive, setSeenActive] = useState(activeRegistrar);
  const [seenTarget, setSeenTarget] = useState(registrarIdno);
  if (seenActive !== activeRegistrar || seenTarget !== registrarIdno) {
    if (seenActive !== activeRegistrar) setPickedRegistrar(null);
    setSeenActive(activeRegistrar);
    setSeenTarget(registrarIdno);
    setReason('');
    setAnswer(null);
  }

  // A medicine from the address is named by the extended search, which ranks the
  // exact code first; a code it does not resolve is shown as the code.
  const medicineLookup = useQuery({
    queryKey: ['uc92-medicine', medicineCode],
    queryFn: () => searchMedicines(medicineCode),
    enabled: medicineCode !== '' && pickedMedicine?.code !== medicineCode,
    staleTime: 5 * 60_000,
  });
  const medicine =
    pickedMedicine?.code === medicineCode
      ? pickedMedicine
      : medicineLookup.data?.items.find((hit) => hit.code === medicineCode);
  // The medicine is the unique one (CF-34), so the brand of the code that happens
  // to be selected is not part of its name.
  const medicineLabel = medicine
    ? formatMedicineLabel({ ...medicine, commercialName: '' })
    : medicineCode || undefined;

  // UC92-03 is a criterion of the query, so its choices come from the register
  // and exist before the query does (audit blocker #7, spec 029 FR-001). A
  // Registrar from the address is revalidated by the server first (D2).
  const lookingUpRegistrar = registrarIdno !== '' && pickedRegistrar?.idno !== registrarIdno;
  const registrarLookup = useQuery({
    queryKey: ['uc92-imsp-registrar', activeRegistrar, registrarIdno],
    queryFn: () => findImspRegistrar(registrarIdno),
    enabled: lookingUpRegistrar,
  });
  const selectedRegistrar: ImspRegistrar | null =
    registrarIdno === ''
      ? null
      : pickedRegistrar?.idno === registrarIdno
        ? pickedRegistrar
        : (registrarLookup.data ?? { idno: registrarIdno, name: registrarIdno });

  useEffect(() => {
    if (!lookingUpRegistrar || !registrarLookup.isSuccess || registrarLookup.data !== null) return;
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(REGISTRAR_PARAM);
        return next;
      },
      { replace: true },
    );
    setRegistrarDropped(true);
  }, [lookingUpRegistrar, registrarLookup.isSuccess, registrarLookup.data, setParams]);

  const query = useMutation({
    mutationFn: (input: { medicineCode: string; registrarIdno: string; reason: string }) =>
      queryCrossImspAvailability(input),
    onSuccess: (result, input) => {
      setAnswer({
        medicineCode: input.medicineCode,
        registrarIdno: input.registrarIdno,
        rows: result,
      });
      setAccessDenied(false);
      setQueryErrorKey(null);
    },
    onError: (error) => {
      // One refusal has a screen state of its own — the denial banner explains
      // who may run this query — and everything else has to be said out loud.
      if (isNormalizedApiError(error) && error.code === 'NOT_IMSP_REGISTRAR') {
        setAccessDenied(true);
        setAnswer(null);
        setQueryErrorKey(null);
        return;
      }
      setQueryErrorKey(apiErrorKey(error, 'crossImspStock.error.query_failed'));
    },
  });

  const rows =
    answer !== null &&
    answer.medicineCode === medicineCode &&
    answer.registrarIdno === registrarIdno
      ? answer.rows
      : null;
  const queriedCode = answer?.medicineCode ?? '';
  const visibleRows = rows ?? [];

  function writeParam(name: string, value: string): void {
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (value) next.set(name, value);
        else next.delete(name);
        return next;
      },
      { replace: true },
    );
  }

  function pickRegistrar(next: ImspRegistrar | null): void {
    setPickedRegistrar(next);
    setRegistrarDropped(false);
    writeParam(REGISTRAR_PARAM, next?.idno ?? '');
  }

  function runQuery(withReason: string): void {
    if (!medicineCode) return;
    query.mutate({ medicineCode, registrarIdno, reason: withReason });
  }

  function openReasonGate(): void {
    setReasonDraft(reason);
    setReasonTouched(false);
    setReasonModalOpen(true);
  }

  function handleSearchClick(): void {
    if (!medicineCode) return;
    if (isSystemAdmin || reason.length >= REASON_MIN_LENGTH) {
      runQuery(reason);
      return;
    }
    openReasonGate();
  }

  // D5: a page loaded with a medicine in its address asks for the reason before
  // it shows anything, with an empty field (TC-ACCS-13-05); the System
  // Administrator's query runs at once (CF-24). Both wait for the session and for
  // the Registrar check, so the query never carries a criterion the server drops.
  const [loadedCode] = useState(medicineCode);
  // A refused Registrar is still in the address until the effect above drops it.
  const registrarRefused = registrarLookup.isSuccess && registrarLookup.data === null;
  const restoring =
    session.isPending || (lookingUpRegistrar && (registrarLookup.isPending || registrarRefused));
  const restoredRef = useRef(false);
  useEffect(() => {
    if (restoredRef.current || restoring) return;
    restoredRef.current = true;
    if (loadedCode === '' || medicineCode !== loadedCode) return;
    if (isSystemAdmin) {
      query.mutate({ medicineCode, registrarIdno, reason: '' });
      return;
    }
    setReasonDraft('');
    setReasonTouched(false);
    setReasonModalOpen(true);
  }, [restoring, loadedCode, medicineCode, registrarIdno, isSystemAdmin, query]);

  function handleReasonConfirm(): void {
    setReasonTouched(true);
    if (reasonTooShort) return;
    const next = reasonDraft.trim();
    setReason(next);
    setReasonModalOpen(false);
    runQuery(next);
  }

  const columns = [
    { key: 'registrar', label: t('crossImspStock.column.registrar'), width: '240px' },
    { key: 'site', label: t('crossImspStock.column.location'), width: '260px' },
    { key: 'lastReported', label: t('crossImspStock.column.last_reported') },
    { key: 'lot', label: t('crossImspStock.column.lot') },
    { key: 'expiry', label: t('crossImspStock.column.expiry') },
    { key: 'quantity', label: t('crossImspStock.column.quantity'), align: 'end' as const },
  ];

  const tableRows = visibleRows.map((row) => ({
    id: `${row.registrarIdno}-${row.siteName}-${row.medicineCode}-${row.lot}`,
    registrar: row.registrarName,
    site: row.siteName,
    // UC92-04 asks for the date of the last reported movement, not its clock time.
    lastReported: formatDate(row.lastReportedAt),
    lot: row.lot,
    expiry: expiryAsGiven(row),
    quantity: formatNumber(row.currentQuantity),
  }));

  return (
    <section className="simsm-cross-imsp">
      <header className="simsm-cross-imsp__header">
        <h1 className="simsm-cross-imsp__title">{t('crossImspStock.title')}</h1>
        <p className="simsm-cross-imsp__lede">{t('crossImspStock.lede')}</p>
      </header>
      <div className="simsm-cross-imsp__controls">
        <label className="simsm-cross-imsp__field simsm-cross-imsp__field--medicine">
          <span>
            {t('crossImspStock.field.medicine')}
            <span aria-hidden="true"> *</span>
          </span>
          <MedicineCombobox
            value={medicineCode}
            pickedLabel={medicineLabel}
            groupByMedicine
            onPick={(hit) => {
              setPickedMedicine(hit);
              setAnswer(null);
              writeParam(MEDICINE_PARAM, hit.code);
            }}
            onClear={() => {
              setPickedMedicine(undefined);
              setAnswer(null);
              writeParam(MEDICINE_PARAM, '');
            }}
          />
        </label>

        {/* The drawing puts the optional target and the action side by side, to
            the right of the search (Verificare.tsx:136, `.imsp-stock-target`).
            Both are query criteria, so both stand before the answer. */}
        <div className="simsm-cross-imsp__target">
          {/* Not a <label>: a click on the picker's list must not be forwarded to
              its field. The picker is named by `ariaLabel`. */}
          <div className="simsm-cross-imsp__field simsm-cross-imsp__field--registrar">
            <span aria-hidden="true">{t('crossImspStock.field.registrar')}</span>
            <ImspRegistrarPicker
              selected={selectedRegistrar}
              ariaLabel={t('crossImspStock.field.registrar')}
              onPick={pickRegistrar}
            />
          </div>

          <MudButton
            shape="circular"
            variant="primary"
            size="md"
            disabled={!medicineCode || query.isPending || restoring}
            onClick={handleSearchClick}
          >
            {t('crossImspStock.action.search')}
          </MudButton>
        </div>

        {!isSystemAdmin && reason ? (
          <p className="simsm-cross-imsp__active-reason">
            {t('crossImspStock.reason.active', { reason })}{' '}
            <MudButton
              shape="circular"
              appearance="text"
              variant="strict"
              size="sm"
              onClick={openReasonGate}
            >
              {t('crossImspStock.reason.change')}
            </MudButton>
          </p>
        ) : null}

        {/* B-01 stays: the journalling notice is on the screen in every state,
            before any query. Its weight is what changes. The drawing states it as
            one grey caption line under the controls (Verificare.tsx:167,
            `.imsp-stock-note`), and the sentence a reader cannot miss belongs at
            the gate — the reason modal opens on it. UC92-01 obliges the system to
            journal and to ask; no caiet clause obliges the screen to shout. */}
        <p className="simsm-cross-imsp__note">
          {isSystemAdmin ? t('crossImspStock.banner.body_admin') : t('crossImspStock.banner.body')}{' '}
          {t('crossImspStock.results.expired_excluded')}
        </p>
      </div>
      {registrarDropped ? (
        <MudInfoBox emphasis="strong" role="alert" variant="warning">
          {t('crossImspStock.notice.registrar_dropped')}
        </MudInfoBox>
      ) : null}
      {accessDenied ? (
        <MudInfoBox
          emphasis="strong"
          role="alert"
          variant="warning"
          titleText={t('crossImspStock.denied.title')}
        >
          {t('crossImspStock.denied.body')}
        </MudInfoBox>
      ) : null}
      {queryErrorKey ? (
        <MudInfoBox emphasis="strong" role="alert" variant="error">
          {t(queryErrorKey)}
        </MudInfoBox>
      ) : null}
      {!accessDenied && queryErrorKey === null && rows === null ? (
        /* The wireframe's empty for this screen (Verificare.tsx:158): it lists
           the five things the answer will hold and repeats that expired lots are
           left out, which is the question every reader asks of this screen. One
           prompt for both roles, and the drawing's own: the sentence that
           promised an inline "și motivul" field named a control neither the
           screen nor the drawing has — the reason is asked at the gate. */
        <EmptyState
          icon="chart"
          title={t('crossImspStock.empty.prompt')}
          body={t('crossImspStock.empty.prompt_body')}
        />
      ) : null}
      {rows !== null ? (
        <>
          <p className="simsm-cross-imsp__excluded-note">
            {t('crossImspStock.results.summary', {
              lots: visibleRows.length,
              sites: new Set(visibleRows.map((row) => `${row.registrarIdno}-${row.siteName}`)).size,
            })}
          </p>

          <MudTable
            className="simsm-table"
            columns={columns}
            rows={tableRows}
            rowStyle="divided"
            hoverable={true}
            disableSort={true}
            aria-label={t('crossImspStock.table.aria_label')}
          >
            {visibleRows.map((row, index) => (
              // UC92-01 as modified 2026-10-12, CF-24: the answer never holds the
              // caller's own Registrar (apps/bff/src/stock/availability.controller.ts),
              // so no row is marked as theirs.
              <span key={`registrar-${index}`} slot={`cell-registrar-${index}`}>
                {row.registrarName}
              </span>
            ))}
            {visibleRows.map((row, index) => (
              <div key={`site-${index}`} slot={`cell-site-${index}`}>
                <div>{row.siteName}</div>
                {row.geographyLabel ? (
                  <div className="simsm-cross-imsp__cell-geography">{row.geographyLabel}</div>
                ) : null}
              </div>
            ))}
            {visibleRows.map((row, index) => (
              <div key={`lot-${index}`} slot={`cell-lot-${index}`}>
                <div>{row.lot}</div>
                {row.medicineCode && row.medicineCode !== queriedCode ? (
                  <div className="simsm-cross-imsp__cell-code">
                    {t('crossImspStock.cell.reported_as', { code: row.medicineCode })}
                  </div>
                ) : null}
              </div>
            ))}
            {/* Borrowed the footnote's class until now, which drew the register's
                one honest sentence at 13px in the same grey as a caption. */}
            <EmptyRegisterMessage>{t('crossImspStock.empty.no_results')}</EmptyRegisterMessage>
          </MudTable>
        </>
      ) : null}
      <MudModal
        closeLabel={t('common.close')}
        open={reasonModalOpen}
        // AGE-BUG(mudClose-bubbles) · vendor/age-design/SOURCE_COMMIT 7322a157 — seven AGE
        // components emit `mudClose` bubbling+composed, so a descendant closing inside this
        // modal reaches this handler too; the guard answers only a real dismissal.
        onMudClose={onModalDismiss(() => setReasonModalOpen(false))}
      >
        <h2 slot="title">{t('crossImspStock.reason.title')}</h2>
        <div className="simsm-cross-imsp__reason">
          {/* The wireframe opens the dialog on an info box (Verificare.tsx:150):
              the audit consequence, stated before the field. The field carries
              its own label, hint and error — AGE's textarea draws all three. */}
          <MudInfoBox emphasis="strong" variant="info">
            {t('crossImspStock.reason.help')}
          </MudInfoBox>
          <MudTextarea
            label={t('crossImspStock.reason.label')}
            required
            rows={3}
            resize="vertical"
            value={reasonDraft}
            placeholder={t('crossImspStock.reason.placeholder')}
            invalid={reasonTouched && reasonTooShort}
            errorText={t('crossImspStock.reason.error.short')}
            helperText={t('crossImspStock.reason.hint')}
            onMudInput={(e: CustomEvent<{ value: string }>) => {
              setReasonDraft(e.detail.value);
              setReasonTouched(true);
            }}
          />
        </div>
        <div slot="actions" className="simsm-cross-imsp__reason-footer">
          <MudButton
            shape="circular"
            variant="neutral"
            size="md"
            onClick={() => setReasonModalOpen(false)}
          >
            {t('crossImspStock.reason.action.cancel')}
          </MudButton>
          <MudButton
            shape="circular"
            variant="primary"
            size="md"
            disabled={reasonTooShort}
            onClick={handleReasonConfirm}
          >
            {t('crossImspStock.reason.action.confirm')}
          </MudButton>
        </div>
      </MudModal>
    </section>
  );
}
